import React, { createContext, useContext, useEffect, useState, useCallback, ReactNode } from "react";
import { contractorDataApi } from "../services/User/contractorDataApi";
import { authorizedAppsForUsersApi } from "../services/User/authorizedAppsForUsersApi";
import isContractor from "../utils/IsContractor";
import xmlParser from "../utils/XMLParser";
import arcticIDGenerator from "../utils/articIdGenerator";
import { useAuth } from "./AuthContext";

interface UserProfile {
  email?: string;
  emplid?: string;
  legacyid?: string;
  company?: string;
  locale?: string;
  name?: string;
}

interface UserAuth {
  userData?: { profile?: UserProfile };
  isLoading?: boolean;
  isAuthenticated?: boolean; 
}

interface UserData {
  email: string;
  PS_ID: string;
  ARCTIC_ID: string;
  Username: string;
  UserStation: string;
  Airline: string;
  AuthorizationAirline: string;
  ContractUser: "1" | "0";
  GetAuthorizedAppsForUser?: string;
  error?: Error;
  isLoading: boolean;
}

interface UserAuthProfile {
  email?: string;
  emplid?: string;
  legacyid?: string;
  company?: string;
  locale?: string;
  name?: string;
  given_name?: string;
  family_name?: string;
}

const UserDataContext = createContext<UserData | undefined>(undefined);

interface UserDataContextProviderProps {
  children: ReactNode;
}

export const UserDataContextProvider: React.FC<UserDataContextProviderProps> = ({ children }) => {
  const userAuth = useAuth() as UserAuth;
  const userDatas = userAuth?.userData;
  const isUserLoading = userAuth?.isLoading;

  const [userData, setUserData] = useState<UserData>({
    email: "",
    PS_ID: "",
    ARCTIC_ID: "",
    Username: "",
    UserStation: "",
    Airline: "",
    AuthorizationAirline: "",
    ContractUser: "0",
    GetAuthorizedAppsForUser: undefined,
    error: undefined,
    isLoading: false,
  });
  const wait= (ms: number) => new Promise(resolve => setTimeout(resolve, ms));
  const configureUserData = useCallback(async () => {
    // logInformation(`Starting to set user Data`, "UserDataContext.tsx", "configureUserData");

    try {
      setUserData((prev) => ({
        ...prev,
        isLoading: true,
      }));

      let email = userDatas?.profile?.email ?? "";
      let Username = userDatas?.profile?.name ?? "";
      let PS_ID = userDatas?.profile?.emplid ?? "";
      let UserStation = userDatas?.profile?.locale ?? "";
      let Airline: string = (userDatas as any)?.profile?.company ?? "";
      let ContractUser = isContractor(PS_ID) as "1" | "0";

      setUserData((prev) => ({
        ...prev,
        email,
      }));

      let ARCTIC_ID = arcticIDGenerator(userDatas || {}, ContractUser);

      if (!ARCTIC_ID?.trim()) {
        throw new Error("ARCTIC_ID is not present");
      }

      if (!UserStation.trim()) {
        UserStation = "SEA";
      }

      if (!Airline.trim()) {
        Airline = "AS";
      }

      setUserData((prev) => ({
        ...prev,
        PS_ID,
        ARCTIC_ID,
        Username,
      }));

      let AuthorizationAirline = ContractUser === "1" ? "C" : Airline;

      let GetAuthorizedAppsForUser: string | undefined;
      await  wait(3000); // Wait for 1 second to ensure the user data is set before making API calls
      // try {
      //   let responseData = await authorizedAppsForUsersApi(ARCTIC_ID, AuthorizationAirline);
      //   localStorage.setItem("authorizedAppsForUsersApi", JSON.stringify(responseData));
      //   GetAuthorizedAppsForUser = xmlParser(JSON.stringify(responseData), "getAuthorizedAppsForUser") as string;
      // } catch (err) {
      //   let cachedUserDataApiResponse = localStorage.getItem("authorizedAppsForUsersApi");
      //   if (cachedUserDataApiResponse !== null) {
      //     GetAuthorizedAppsForUser = xmlParser(cachedUserDataApiResponse, "getAuthorizedAppsForUser") as string;
      //   }
      // }

      // if (ContractUser === "1") {
      //   try {
      //     const responseData = await contractorDataApi(ARCTIC_ID);
      //     localStorage.setItem("contractorDataApi", JSON.stringify(responseData));
      //     interface RawDataType {
      //       UserStation: string;
      //       Airline: string;
      //     }
      //     let rawData: RawDataType = xmlParser(JSON.stringify(responseData), "getContractorData") as RawDataType;
      //     if (rawData?.UserStation && rawData?.Airline) {
      //       UserStation = rawData.UserStation;
      //       Airline = rawData.Airline;
      //     }
      //   } catch (err) {
      //     let cachedContractorDataApiResponse = localStorage.getItem("contractorDataApi");
      //     if (cachedContractorDataApiResponse !== null) {
      //       interface RawDataType {
      //         UserStation: string;
      //         Airline: string;
      //       }
      //       let rawData: RawDataType = xmlParser(cachedContractorDataApiResponse, "getContractorData") as RawDataType;
      //       if (rawData?.UserStation && rawData?.Airline) {
      //         UserStation = rawData.UserStation;
      //         Airline = rawData.Airline;
      //       }
      //     }
      //   }
      // }

      setUserData((prev) => ({
        ...prev,
        UserStation: UserStation || "SEA",
        Airline: Airline || "AS",
        AuthorizationAirline,
        ContractUser,
        GetAuthorizedAppsForUser,
        isLoading: false,
      }));

    } catch (err) {
      setUserData((prev) => ({
        ...prev,
        isLoading: false,
        error: err instanceof Error ? err : new Error(String(err)),
      }));
    }
  }, [userDatas]);

  useEffect(() => {
    if (userDatas && !isUserLoading) {
      configureUserData();
    }
  }, [isUserLoading, configureUserData, userDatas]); // Add configureUserData to dependencies

  return (
    <UserDataContext.Provider value={{ ...userData }}>
      {children}
    </UserDataContext.Provider>
  );
};

export const useUserDataConsumer = (): UserData => {
  const context = useContext(UserDataContext);

  if (!context) {
    throw new Error("useUserDataConsumer must be used within a UserDataContextProvider");
  }
  return context;
};



export const useUserDataAuthConsumer = (): UserAuthProfile => {
  const [profileData, setProfileData] = useState<UserAuthProfile>({});
  let storedDataString: string | null = null;
  useEffect(() => {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('oidc.user:')) {
        storedDataString = localStorage.getItem(key);
        break;
      }
    }

    if (storedDataString) {
      try {
        const storedData = JSON.parse(storedDataString);
        const profile: UserAuthProfile = {
          email: storedData.profile.email,
          emplid: storedData.profile.emplid,
          legacyid: storedData.profile.legacyid,
          company: storedData.profile.company,
          locale: storedData.profile.locale,
          name: storedData.profile.name,
          given_name: storedData.profile.given_name,
          family_name: storedData.profile.family_name
        };
        setProfileData(profile);
      } catch (error) {
        console.error("Error parsing local storage data:", error);
      }
    }
  }, []);

  return profileData;
};
